import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiResponse, Money } from '@app/shared/interfaces';
// import { Money } from '@app/shared/interfaces/lib/money.interface';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export type ProductType = 'early_checkin' | 'late_checkout' | 'extra_cleaning' | 'custom';

export type PricingType = 'fixed' | 'per_night';

export interface ProductVariation {
  id: string;
  name: string;
  price: Money;
  active: boolean;
  metadata: {
    hours: number;
  };
}

export interface ProductItem {
  id: string;
  type: ProductType;
  pricing_type: PricingType;
  public_name: string;
  private_name: string;
  description: string;

  active: boolean;
  variations: ProductVariation[];

  all_properties: boolean;
  properties: number[];
  property_list: { name: string; picture: string }[];

  all_hosts: boolean;
  hosts: string[];
  host_list: { id: string; name: string }[];

  all_platforms: boolean;
  platforms: string[];

  prebooking: boolean;
  blocks_day: boolean;
  timing_buffer: {
    value: number;
    unit: 'hours' | 'days';
  };
}

export interface ProductCategory {
  id: string;
  name: string;
  products: ProductItem[];
}

export interface SmartUpsellRule {
  id: string;
  name: string;
  event_short: string;
  all_properties: boolean;
  properties: number[];
  all_hosts: boolean;
  hosts: string[];
  active_hosts_count?: number;
  platforms: string[];
  active: boolean;
}

interface CreateProductPayload {
  type: ProductType;
  currency: string;
  name?: string;
  pricing_type?: PricingType;
}

@Injectable({
  providedIn: 'root',
})
export class ProductCatalogService {
  constructor(
    @Inject('apiUrl') private apiUrl: string,
    private http: HttpClient
  ) {}

  setupDefaultProducts(): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/products/default`, {});
  }

  getProductCategories(): Observable<ApiResponse<ProductCategory[]>> {
    return this.http.get<ApiResponse<ProductCategory[]>>(`${this.apiUrl}/products`);
  }

  getProduct(id: string): Observable<ApiResponse<ProductItem>> {
    return this.http.get<ApiResponse<ProductItem>>(`${this.apiUrl}/products/${id}`);
  }

  updateProduct(product: ProductItem): Observable<ApiResponse<ProductItem>> {
    return this.http.put<ApiResponse<ProductItem>>(`${this.apiUrl}/products/${product.id}`, product);
  }

  deleteProduct(id: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.apiUrl}/products/${id}`);
  }

  updateProductVariation(
    productId: string,
    variationId: string,
    data: { price: number }
  ): Observable<ApiResponse<any>> {
    return this.http.put<ApiResponse<any>>(`${this.apiUrl}/products/${productId}/variation/${variationId}`, data);
  }

  createNewProduct(
    type: ProductType,
    currency: string,
    name?: string,
    pricingType?: PricingType
  ): Observable<ApiResponse<ProductItem>> {
    const payload: CreateProductPayload = { type, currency };

    if (type === 'custom') {
      if (!name || !pricingType) {
        throw new Error('Name and pricing model are required for custom products');
      }
      payload.name = name;
      payload.pricing_type = pricingType;
    }

    return this.http.post<ApiResponse<any>>(`${this.apiUrl}/products`, payload);
  }

  activeOrDeactivateProduct(id: string, activate: boolean) {
    const mode = activate ? 'activate' : 'deactivate';

    const url = `${this.apiUrl}/products/${id}/${mode}`;

    return this.http.put(url, {}).pipe(
      map((res) => res),
      catchError((err: HttpErrorResponse) => of(err))
    );
  }

  activateProductVariation(productId: string, variationId: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/products/${productId}/variation/${variationId}/activate`, {}).pipe(
      map((res) => res),
      catchError((err: HttpErrorResponse) => of(err))
    );
  }

  deactivateProductVariation(productId: string, variationId: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/products/${productId}/variation/${variationId}/deactivate`, {}).pipe(
      map((res) => res),
      catchError((err: HttpErrorResponse) => of(err))
    );
  }

  getSmartUpsellRules(): Observable<SmartUpsellRule[]> {
    return this.http.get<ApiResponse<SmartUpsellRule[]>>(`${this.apiUrl}/products/smart-upsell-rules`).pipe(
      map((response) => {
        return response.data;
      })
    );
  }
}
